// import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card.tsx"
import "./PfInterface.css"

const PAGE_TITLE = "Plasmodium falciparum"

// const BASE_PATH = "/modelling/pf"

type CardVariant = "data" | "model" | "result"

interface PipelineCardProps {
  to: string
  variant: CardVariant
  children: React.ReactNode
  className?: string
  boxClass?: string
}

const VARIANT_CLASSES: Record<CardVariant, string> = {
  data: "bg-gray-50   border-gray-200   text-foreground   hover:bg-gray-100   dark:bg-gray-350/40  dark:border-gray-900  dark:text-background",
  model:
    "bg-amber-100  border-amber-300  text-foreground  hover:bg-amber-200  dark:bg-amber-900/60 dark:border-amber-800 dark:text-foreground",
  result:
    "bg-foreground/80  border-black  text-background  hover:bg-foreground dark:bg-gray-950/40  dark:border-gray-500  dark:text-foreground",
}

function PipelineCard({  variant, children, className, boxClass }: PipelineCardProps): React.JSX.Element {
  return (
    <div className={cn("relative",boxClass)}>
      <div
        // to={`${BASE_PATH}${to}`}
        className={cn(
          "cursor-default no-underline flex items-center justify-center rounded-md border px-3 py-2 text-center text-sm font-semibold transition-colors w-full",
          VARIANT_CLASSES[variant],
          className
        )}
      >
        {children}
      </div>
    </div>
  )
}


export default function PfInterface() {

return (
  <>
  <title>{PAGE_TITLE} - MAP Apps Platform</title>
  <div className="flex flex-1 min-h-0">
    {/* Main content — table card */}
    <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
      <CardContent className="flex flex-col h-full justify-center pb-2">
        <div className="max-w-4xl mx-auto w-full space-y-6">
          {/* Row 1 — data inputs */}
          <div className="grid grid-cols-4 gap-6">
            <PipelineCard to="/intervention_data" variant="data" boxClass="arrow-box">
              Intervention data
            </PipelineCard>
            <PipelineCard to="/pfpr_data" variant="data" boxClass="arrow-box">
              <em>Pf</em>PR data
            </PipelineCard>
            <PipelineCard to="/data/covariates" variant="data" boxClass="arrow-box">
              Covariates
            </PipelineCard>
            <PipelineCard to="/data/rs/hf" variant="data" boxClass="arrow-box">
              Routine Surveillance
            </PipelineCard>
          </div>

          {/* Row 2 — model steps */}
          <div className="grid grid-cols-4 gap-6">
            <PipelineCard
              to="/coverage_models"
              variant="model"
              className="py-7.75"
              boxClass="arrow-box"
            >
              Coverage models
            </PipelineCard>
            <div className="flex flex-col gap-2">
              <PipelineCard to="/age_model" variant="model">
                Age Model
              </PipelineCard>
              <PipelineCard to="/diagnostic_model" variant="model" boxClass="arrow-box">
                Diagnostic Model
              </PipelineCard>
            </div>
            <PipelineCard
              to="/covariates_models"
              variant="model"
              className="py-7.75"
              boxClass="arrow-box"
            >
              Covariate models
            </PipelineCard>
            <PipelineCard
              to="/adjustments"
              variant="model"
              className="py-7.75"
              boxClass="arrow-box"
            >
              Adjustments
            </PipelineCard>
          </div>

          {/* Row 3 — intermediate results */}
          <div className="grid grid-cols-4 gap-6">
            <PipelineCard to="/geospatial_coverage" variant="result" boxClass="arrow-box">
              Geospatial coverage
            </PipelineCard>
            <PipelineCard to="/standardised_pfpr" variant="result" boxClass="arrow-box">
              Standardised <em>Pf</em>PR
            </PipelineCard>
            <PipelineCard to="/derived_covariates" variant="result" boxClass="arrow-box">
              Derived covariates
            </PipelineCard>
            <PipelineCard to="/pseudo_response" variant="result" boxClass="arrow-box">
              Pseudo-response
            </PipelineCard>
          </div>

          {/* Row 4 — main model */}
          <PipelineCard to="/pfpr_model" variant="model" className="py-4" boxClass="arrow-box">
            Bayesian geostatistical model for <em className="mx-1">Pf</em>PR
          </PipelineCard>

          {/* Row 5 — PfPR output */}
          <PipelineCard to="/pfpr" variant="result" className="py-4" boxClass="arrow-box">
            <em>Pf</em>PR
          </PipelineCard>

          {/* Row 6 — incidence conversion */}
          <div className="grid grid-cols-4 gap-6 items-start">
            <div className="flex flex-col gap-2">
              <PipelineCard to="/incidence_data" variant="data" boxClass="arrow-box__right">
                Incidence data
              </PipelineCard>
              <PipelineCard to="/data/seasonality" variant="data" boxClass="arrow-box__right">
                Seasonality
              </PipelineCard>
              <PipelineCard to="/tx" variant="data" boxClass="arrow-box__right">
                Tx
              </PipelineCard>
            </div>
            <div className="col-span-2">
              <PipelineCard
                to="/pfpr_incidence_model"
                variant="model"
                className="py-6 h-32.5"
                boxClass="arrow-box"
              >
                <em>Pf</em>PR to incidence conversion model
              </PipelineCard>
            </div>
            <div className="flex flex-col gap-2">
              <PipelineCard to="/data/interventions/smc" variant="data" boxClass="arrow-box__left">
                SMC
              </PipelineCard>
              <PipelineCard to="/exposure" variant="data" boxClass="arrow-box__left">
                Exposure
              </PipelineCard>
            </div>
          </div>

          {/* Row 7 — final output */}
          <PipelineCard to="/pf_case_incidence" variant="result" className="py-4">
            <em>Pf</em> Case incidence
          </PipelineCard>
        </div>
      </CardContent>
    </Card>
  </div>
  </>
)}